import { Component, Input, OnInit, ViewChild } from '@angular/core';
import { UntypedFormBuilder, FormControl, UntypedFormGroup } from '@angular/forms';
import { MatPaginator } from '@angular/material/paginator';
import { MatSort } from '@angular/material/sort';
import { MatTableDataSource } from '@angular/material/table';
import { from, merge } from 'rxjs';
import { debounceTime, filter, map, switchMap, tap } from 'rxjs/operators';

import { PreferenceService } from '../../core';
import { GetGrievanceListParams, GrievanceListItemModel } from '../models/grievances.models';
import { GrievancesRepositoryService } from '../grievances.repository';
import { ConfirmationService } from '../../shared';

@Component({
   selector: 'am-grievance-list',
   templateUrl: 'grievance-list.component.html',
   styleUrls: ['grievance-list.component.scss']
})
export class GrievanceListComponent implements OnInit {
   @ViewChild(MatSort, { static: true }) sort: MatSort;
   @ViewChild(MatPaginator, { static: true }) paginator: MatPaginator;

   dataSource = new MatTableDataSource<GrievanceListItemModel>([]);
   loading = true;
   columnsToDisplay = ['grievant', 'case', 'program', 'status',
      'fileDate', 'escalationDate', 'replyDueDate', 'actualReplyDate', 'actions'];
   returnedItemsCount = 0;
   grievances: GrievanceListItemModel[];
   filterForm: UntypedFormGroup;
   activeFilter = true;
   dateFilter = {};
   showFilters = false;

   constructor(
      private formBuilder: UntypedFormBuilder,
      private grievanceService: GrievancesRepositoryService,
      private preferenceService: PreferenceService,
      private confirmation: ConfirmationService) {
   }

   ngOnInit() {
      this.initialize();
      this.subscribe();
      this.getGrievanceList();
   }

   toggleFilters() {
      this.showFilters = !this.showFilters;
   }

   getParameters() {
      const order = this.sort.active || 'fileDate';
      this.dateFilter = this.filterForm.get('dateFilter').value;
      this.activeFilter = this.filterForm.get('activeFilter').value;
      this.paginator.pageSize = this.paginator.pageSize || 25;

      this.savePreferences();

      const params: GetGrievanceListParams = {
         order: this.sort.direction === 'desc' ? '-' + order : order,
         page: this.paginator.pageIndex + 1,
         pageSize: this.paginator.pageSize,
         ...this.dateFilter,
         isActive: this.activeFilter
      };

      return params;
   }

   getGrievanceList() {
      this.dataSource.data = [];

      this.loading = true;
      const params = this.getParameters();

      return this.grievanceService.getGrievanceList(params)
         .subscribe(data => {
            this.loading = false;
            this.returnedItemsCount = data.count;
            this.dataSource.data = data.list;
         });
   }

   export = () => {
      const params = this.getParameters();
      this.grievanceService.getGrievanceListDownload(params)
         .subscribe();
   }

   private subscribe() {
      this.sort.sortChange.subscribe(() => this.paginator.pageIndex = 0);
      merge(this.sort.sortChange, this.paginator.page, this.filterForm.valueChanges)
      .pipe(
         debounceTime(500)
      )
      .subscribe(() => this.getGrievanceList());
   }

   private initialize() {
      this.loadPreferences();

      this.filterForm = this.formBuilder.group({
         dateFilter: this.dateFilter,
         activeFilter: this.activeFilter,
         grievanceNameFilter: null
      });
   }

   private loadPreferences(): any {
      const preferences: any = this.preferenceService.getPreference('grievanceList');
      if (preferences) {
         Object.assign(this.sort, preferences.sortSettings);
         Object.assign(this.paginator, preferences.paginatorSettings);
         this.activeFilter = preferences.activeFilter;
         Object.assign(this.dateFilter, preferences.dateFilter);
      }
   }

   private savePreferences() {
      const preferences = {
         sortSettings: { active: this.sort.active, direction: this.sort.direction },
         paginatorSettings: { pageSize: this.paginator.pageSize },
         dateFilter: this.dateFilter,
         activeFilter: this.activeFilter,
      };
      this.preferenceService.setPreference('grievanceList', preferences);
   }

   showDeleteDialog(grievance: GrievanceListItemModel) {
      this.confirmation.confirm('Delete Grievance?', 'DELETE', 'CANCEL').pipe(
         filter(ok => ok),
         switchMap(() => this.grievanceService.deleteGrievance(grievance.id))
      ).subscribe(() => {
         const index = this.dataSource.data.indexOf(grievance);
         this.dataSource.data = [
            ...this.dataSource.data.slice(0, Math.max(index, 0)),
            ...this.dataSource.data.slice(index + 1)
         ];
      });
   }

   clearFilters() {
      this.filterForm.patchValue({
         dateFilter: {},
         activeFilter: null,
         grievanceNameFilter: null
      });
   }
}



















